import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "primereact/button";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { SelectButton } from "primereact/selectbutton";
import { Skeleton } from "primereact/skeleton";
import { Tag } from "primereact/tag";
import { SeleccionarFamiliasDialog } from "../components/dialogs/SeleccionarFamiliasDialog";
import AppToast from "../../../shared/components/AppToast";
import { ensureCatalogAuth } from "../../../services/backend/authService";
import { getCategoriasConfig, editarCategoriasConfig, } from "../../../services/backend/tenantConfigService";
import { getCompany } from "../../../config/companyConfig";
import { moverElementoEnString } from "../../../utils/orderUtils";


const CANALES_OPCIONES = [
  { label: "B2B", value: "B2B" },
  { label: "B2C", value: "B2C" },
];

const buildConfigFallback = (canalKey) => {
  const company = getCompany();
  const canalCfg = company.categorias?.[canalKey] || {};
  return {
    limite: canalCfg.limite,
    tipo: canalCfg.tipo,
    title: canalCfg.title || "",
    subtitle: canalCfg.subtitle || "",
    ids: canalCfg.ids || "",
  };
};

const extractCanalCfg = (categorias, canalKey) => {
  if (!categorias) return null;
  if (categorias[canalKey]) return categorias[canalKey];
  return categorias;
};

const Categorias = () => {
  const toastRef = useRef(null);
  const tenantId = getCompany().id;
  const [canal, setCanal] = useState(getCompany().defaultCanal);
  const [config, setConfig] = useState(null);
  const [draft, setDraft] = useState(null);
  const [opciones, setOpciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editando, setEditando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [dialogVisible, setDialogVisible] = useState(false);

  const showToast = (severity, summary, detail) => {
    toastRef.current?.show({ severity, summary, detail, life: 4000 });
  };

  const cargarConfig = useCallback(async (canalParam) => {
    setLoading(true);
    setEditando(false);
    const ck = canalParam.toLowerCase();
    const fallback = buildConfigFallback(ck);
    try {
      await ensureCatalogAuth();
      const res = await getCategoriasConfig(tenantId, canalParam);
      if (res?.resultado) {
        const canalCfg = extractCanalCfg(res.categorias, ck);
        const cfg = {
          limite: canalCfg?.limite,
          tipo: canalCfg?.tipo,
          title: canalCfg?.title || "",
          subtitle: canalCfg?.subtitle || "",
          ids: canalCfg?.ids || "",
        };
        setConfig(cfg);
        setDraft(cfg);
        setOpciones(
          Array.isArray(res.tipoCategoriasOpciones) &&
            res.tipoCategoriasOpciones.length
            ? res.tipoCategoriasOpciones
            : fallback.opciones
        );
      } else {
        setConfig({ ...fallback });
        setDraft({ ...fallback });
        setOpciones(fallback.opciones);
      }
    } catch (err) {
      setConfig({ ...fallback });
      setDraft({ ...fallback });
      setOpciones(fallback.opciones);
      showToast(
        "error",
        "Error",
        err?.message || "No se pudo cargar la configuración."
      );
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let mounted = true;
    const run = async () => {
      if (mounted) await cargarConfig(canal);
    };
    run();
    return () => {
      mounted = false;
    };
  }, [canal, cargarConfig]);

  const onCanalChange = (e) => {
    if (e.value && e.value !== canal) {
      setCanal(e.value);
    }
  };

  const onEditar = () => {
    setDraft(config);
    setEditando(true);
  };

  const onCancelar = () => {
    setDraft(config);
    setEditando(false);
  };

  const onGuardar = async () => {
    if (draft.limite == null) {
      showToast("warn", "Faltan datos", "El límite es obligatorio.");
      return;
    }
    if (!draft.tipo) {
      showToast("warn", "Faltan datos", "El tipo es obligatorio.");
      return;
    }
    setGuardando(true);
    try {
      await ensureCatalogAuth();
      await editarCategoriasConfig({
        tenant_id: tenantId,
        canal,
        limite: draft.limite,
        tipo: draft.tipo,
        title: draft.title,
        subtitle: draft.subtitle,
        ids: draft.ids,
      });
      await cargarConfig(canal);
      setEditando(false);
      showToast(
        "success",
        "Configuración guardada",
        "La configuración de categorías fue actualizada."
      );
    } catch (err) {
      showToast(
        "error",
        "Error",
        err?.message || "No se pudo guardar la configuración."
      );
    } finally {
      setGuardando(false);
    }
  };

  const onCampoChange = (campo, valor) => {
    setDraft((prev) => ({ ...prev, [campo]: valor }));
  };

  const moverId = (posicion, direccion) => {
    onCampoChange("ids", moverElementoEnString(draft.ids, posicion, direccion));
  };

  const tipoLabel =
    opciones.find((o) => o.value === config?.tipo)?.label || config?.tipo || "";

  return (
    <>
      <AppToast ref={toastRef} />
      <SeleccionarFamiliasDialog
        visible={dialogVisible}
        onHide={() => setDialogVisible(false)}
        onConfirm={(ids) => onCampoChange("ids", ids)}
        idsIniciales={draft?.ids || ""}
        tipo={canal}
      />
      <div className="py-3">
        <div className="grid">
          <div className="col-12">
            <div className="COL-12">
              <h4 className="font-bold mb-0">
                Gestión de Categorías Destacadas
              </h4>
              <p className="text-700 text-lg line-height-3 mb-3">
                Configura el tipo, límite, título, subtítulo y familias
                que se muestran en la sección de categorías del Home.
              </p>
            </div>
            <div className="grid">
              <div className="col-12 md:col-6">
                <div className="border-1 surface-border border-round p-4">
                  <div className="flex align-items-center justify-content-between mb-3">
                    <span className="text-xl font-semibold">
                      Configuración actual
                    </span>
                    <SelectButton
                      value={canal}
                      options={CANALES_OPCIONES}
                      onChange={onCanalChange}
                    />
                    {!editando && !loading && (
                      <Button
                        icon="pi pi-pencil"
                        label="Editar"
            
                        onClick={onEditar}
                      />
                    )}
                  </div>

                  {loading ? (
                    <div className="flex flex-column gap-3">
                      <div className="flex align-items-center justify-content-between">
                        <span className="text-700">Tipo activo</span>
                        <Skeleton width="6rem" height="1.5rem" />
                      </div>
                      <div className="flex align-items-center justify-content-between">
                        <span className="text-700">Límite</span>
                        <Skeleton width="4rem" height="1.5rem" />
                      </div>
                      <div className="flex align-items-center justify-content-between">
                        <span className="text-700">Título</span>
                        <Skeleton width="10rem" height="1.5rem" />
                      </div>
                      <div className="flex align-items-center justify-content-between">
                        <span className="text-700">Subtítulo</span>
                        <Skeleton width="10rem" height="1.5rem" />
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-column gap-3">
                      <div className="flex align-items-center justify-content-between">
                        <span className="text-700">Canal</span>
                        <Tag value={canal} severity="info" />
                      </div>
                      <div className="flex align-items-center justify-content-between">
                        <span className="text-700">Tipo activo</span>
                        <Tag value={tipoLabel} severity="info" />
                      </div>
                      <div className="flex align-items-center justify-content-between">
                        <span className="text-700">Límite</span>
                        <Tag value={config.limite} />
                      </div>
                      <div className="flex align-items-center justify-content-between">
                        <span className="text-700">Título</span>
                        <Tag value={config.title || "—"} />
                      </div>
                      <div className="flex align-items-center justify-content-between">
                        <span className="text-700">Subtítulo</span>
                        <Tag value={config.subtitle || "—"} />
                      </div>
                      {config.tipo !== "masVendidas" && config.ids && (
                        <div className="flex flex-column gap-1">
                          <span className="text-700">Familias</span>
                          <div className="flex flex-wrap gap-1">
                            {config.ids.split(",").map((id) => (
                              <Tag
                                key={id}
                                value={id.trim()}
                                severity="secondary"
                              />
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {editando && !loading && (
                <div className="col-12 md:col-6">
                  <div className="border-1 surface-border border-round p-4">
                    <div className="flex align-items-center justify-content-between mb-3">
                      <span className="text-xl font-semibold">
                        Editar configuración
                      </span>
                    </div>

                    <div className="flex flex-column gap-4">
                      <div>
                        <label className="text-700 block mb-2">
                          Tipo de categorías
                        </label>
                        <SelectButton
                          value={draft.tipo}
                          options={opciones}
                          onChange={(e) => onCampoChange("tipo", e.value)}
                        />
                      </div>

                      <div>
                        <label className="text-700 block mb-2">
                          Límite de categorías
                        </label>
                        <InputNumber
                          value={draft.limite}
                          onValueChange={(e) =>
                            onCampoChange("limite", e.value)
                          }
                          min={1}
                          showButtons
                          size={5}
                          tooltip="Número máximo de categorías que se mostrarán."
                          tooltipOptions={{ position: "top" }}
                        />
                      </div>

                      <div>
                        <label className="text-700 block mb-2">
                          Título
                        </label>
                        <InputText
                          value={draft.title}
                          onChange={(e) => onCampoChange("title", e.target.value)}
                          className="w-full"
                          placeholder="Ej: Nuestros Productos"
                        />
                      </div>

                      <div>
                        <label className="text-700 block mb-2">
                          Subtítulo
                        </label>
                        <InputText
                          value={draft.subtitle}
                          onChange={(e) => onCampoChange("subtitle", e.target.value)}
                          className="w-full"
                          placeholder="Ej: categorías recomendados"
                        />
                      </div>

                      {draft.tipo !== "masVendidas" && (
                      <div className="flex flex-column gap-2">
                        <label className="text-700 block">
                          Familias (IDs)
                        </label>
                        {draft.ids && (
                          <div className="flex flex-wrap gap-1 mb-2">
                            {draft.ids.split(",").map((id, index, arr) => (
                              <div key={id} className="flex align-items-center gap-1">
                                <Button
                                  icon="pi pi-chevron-left"
                                  className="p-button-rounded p-button-text p-button-sm"
                                  onClick={() => moverId(index, -1)}
                                  disabled={index === 0}
                                />
                                <Tag value={id.trim()} severity="secondary" />
                                <Button
                                  icon="pi pi-chevron-right"
                                  className="p-button-rounded p-button-text p-button-sm"
                                  onClick={() => moverId(index, 1)}
                                  disabled={index === arr.length - 1}
                                />
                              </div>
                            ))}
                          </div>
                        )}
                        <Button
                          icon="pi pi-search"
                          label={
                            draft.ids
                              ? "Modificar familias"
                              : "Seleccionar familias"
                          }
                          severity="info"
                          outlined
                          onClick={() => setDialogVisible(true)}
                        />
                      </div>
                      )}

                      <div className="flex gap-2 justify-content-end">
                        <Button
                          label="Cancelar"
                          icon="pi pi-times"
                          severity="danger"
                          onClick={onCancelar}
                          disabled={guardando}
                        />
                        <Button
                          label="Guardar"
                          icon="pi pi-check"
                          severity="success"
                          onClick={onGuardar}
                          loading={guardando}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Categorias;
